import {
  alignTypes,
  fittingTypes,
  getData,
  isWEBP,
  getFileExtension,
  fileType,
} from '@wix/image-kit';
import type { ImageTransformSource, ImageDataAttributes } from '@wix/image-kit';
import { camelToKebab } from '../utils';
import { EnvConsts, MediaSizeQuery } from '../types';
import { CSSProperties } from 'react';

const CSS_NUMERIC_VALUES = {
  columnCount: 1,
  columns: 1,
  fontWeight: 1,
  lineHeight: 1,
  opacity: 1,
  zIndex: 1,
  zoom: 1,
};

const pick = <T extends Record<string, any>, K extends keyof T>(
  obj: T,
  props: K | K[],
) => {
  const propsArr = Array.isArray(props) ? props : [props];
  return propsArr.reduce((subObj, prop) => {
    const val = obj[prop];
    return val !== undefined ? Object.assign(subObj, { [prop]: val }) : subObj;
  }, {} as Partial<Pick<T, K>>);
};

const addDefaultUnitIfNeeded = (
  prop: keyof React.CSSProperties,
  value: Exclude<CSSProperties[keyof CSSProperties], undefined>,
): string =>
  typeof value === 'number' &&
  !CSS_NUMERIC_VALUES[prop as keyof typeof CSS_NUMERIC_VALUES]
    ? `${value}px`
    : value.toString();

const setStyle = (
  node?: HTMLElement | HTMLImageElement,
  styleProperties?: React.CSSProperties,
) =>
  node &&
  styleProperties &&
  Object.keys(styleProperties).forEach((prop) => {
    const styleProp = prop as keyof React.CSSProperties;
    const propValue = styleProperties[styleProp];

    if (propValue !== undefined) {
      node.style[styleProp as any] = addDefaultUnitIfNeeded(
        styleProp,
        propValue,
      );
    } else {
      node.style.removeProperty(styleProp);
    }
  });

const getScreenHeight = (heightOverride?: number) =>
  heightOverride ||
  document.documentElement.clientHeight ||
  window.innerHeight ||
  0;

const matchesUserDomainMediaPrefix = (
  uri: string,
  prefixes: string[],
): boolean => prefixes.some((prefix) => uri.startsWith(`${prefix}_`));

const ensureTrailingSlash = (value: string): string =>
  value.endsWith('/') ? value : `${value}/`;

const resolveStaticMediaBaseUrl = ({
  uri,
  envConsts,
}: {
  uri: string;
  envConsts: EnvConsts;
}): string => {
  const {
    externalBaseUrl,
    userDomainMediaPrefixes = [],
    staticMediaUrl,
  } = envConsts;

  if (
    matchesUserDomainMediaPrefix(uri, userDomainMediaPrefixes) &&
    externalBaseUrl
  ) {
    return `${ensureTrailingSlash(externalBaseUrl)}_media/`;
  }

  return ensureTrailingSlash(staticMediaUrl);
};

const getImageComputedProperties = (
  extendedImageInfo: Record<string, any>,
  envConsts: EnvConsts,
  htmlTag: keyof JSX.IntrinsicElements,
): ImageDataAttributes => {
  // todo: CLNT-5323 , wixapp sildergallery proxy is generating image data without uri
  if (
    !extendedImageInfo.targetWidth ||
    !extendedImageInfo.targetHeight ||
    !extendedImageInfo.imageData.uri
  ) {
    return { uri: '', css: {}, transformed: false };
  }

  const { imageData } = extendedImageInfo;
  const fittingType =
    extendedImageInfo.displayMode || fittingTypes.SCALE_TO_FILL;
  const imageOptions = Object.assign(
    pick(imageData, ['upscaleMethod']),
    pick(extendedImageInfo, [
      'filters',
      'encoding',
      'allowFullGIFTransformation',
      'allowWebpAvifTransforms',
    ]),
    extendedImageInfo.quality || imageData.quality,
    {
      hasAnimation: extendedImageInfo?.hasAnimation || imageData?.hasAnimation,
    },
  );

  const devicePixelRatioFromData =
    extendedImageInfo.imageData.devicePixelRatio || envConsts.devicePixelRatio;
  const devicePixelRatio = getDevicePixelRatio(devicePixelRatioFromData);

  const src = Object.assign(
    pick(imageData, ['width', 'height', 'crop', 'name', 'focalPoint']),
    { id: imageData.uri },
  ) as ImageTransformSource;
  const target = {
    width: extendedImageInfo.targetWidth,
    height: extendedImageInfo.targetHeight,
    htmlTag: (htmlTag || 'img') as 'img' | 'bg',
    pixelAspectRatio: devicePixelRatio,
    alignment: extendedImageInfo.alignType || alignTypes.CENTER,
  };

  const imageComputedProperties = getData(
    fittingType,
    src,
    target,
    imageOptions,
  );

  const staticMediaUrl = imageData.userDomainMediaURL
    ? imageData.userDomainMediaURL
    : resolveStaticMediaBaseUrl({
        uri: imageData.uri,
        envConsts,
      });

  imageComputedProperties.uri = getMediaUrlByContext(
    imageComputedProperties.uri as string,
    staticMediaUrl,
    envConsts.mediaRootUrl,
  );
  return imageComputedProperties;
};

const getMediaUrlByContext = (
  imageUri: string,
  staticMediaUrl: string,
  mediaRootUrl: string,
) => {
  const isExternalUrl = /(^https?)|(^data)|(^blob)|(^\/\/)/.test(imageUri);

  if (isExternalUrl) {
    return imageUri;
  }

  let path = ensureTrailingSlash(staticMediaUrl);

  if (imageUri) {
    if (/^micons\//.test(imageUri)) {
      path = ensureTrailingSlash(mediaRootUrl);
    } else if (/[^.]+$/.exec(imageUri)?.[0] === 'ico') {
      // if the image is an icon then it's taken from a slightly different place
      path = path.replace('media', 'ficons');
    }
  }
  return path + imageUri;
};

const getDevicePixelRatio = (devicePixelRatio?: number) => {
  // we should be able to force devicePixelRatio from url by using the query param -
  const queryParams = window.location.search
    .split('&')
    .map((query) => query.split('='));
  const devicePixelRatioQueryParam = queryParams.find((query) =>
    query[0]?.toLowerCase().includes('devicepixelratio'),
  );
  const devicePixelRatioValueForceFromUrl = devicePixelRatioQueryParam?.[1]
    ? Number(devicePixelRatioQueryParam[1])
    : null;
  return devicePixelRatioValueForceFromUrl || devicePixelRatio || 1;
};

const getImageSrc = (imageNode: HTMLImageElement) =>
  imageNode.getAttribute('src');

const isTransformedWEBP = (imageNode: HTMLImageElement, imageUri: string) => {
  const src = getImageSrc(imageNode) || '';
  const isTransformed = !!src.match(/.webp\/v1\//);
  return isWEBP(imageUri) && isTransformed;
};

const imageIsAnimated = (uri: string, hasAnimation?: boolean) =>
  getFileExtension(uri) === fileType.GIF ||
  (getFileExtension(uri) === fileType.WEBP && hasAnimation);

const getMediaSizeQueryString = (media: MediaSizeQuery) => {
  return Object.entries(media)
    .filter(([_, value]) => value || value === 0)
    .map(([key, value]) => `(${camelToKebab(key)}: ${value}px)`)
    .join(' and ');
};

export {
  // TODO(ameerabuf) - move methods that are not custom-element logic to a more general image util lib
  getMediaSizeQueryString,
  getMediaUrlByContext,
  getScreenHeight,
  setStyle,
  getImageComputedProperties,
  getImageSrc,
  isTransformedWEBP,
  imageIsAnimated,
};
